<template>
  <div class="app-container qr-page">
    <div class="page-head">
      <div class="page-title">我的常用语</div>
      <div class="page-actions">
        <el-button icon="el-icon-upload2" @click="openImportModal">批量导入</el-button>
        <el-button icon="el-icon-download" :loading="exporting" @click="handleExport">导出全部</el-button>
      </div>
    </div>
    <p class="page-desc">管理个人快捷用语；在「我的会话」聊天窗口可通过侧栏或输入 / 关键字快速插入。</p>

    <div class="qr-layout">
      <!-- 左侧分组 -->
      <div class="qr-sider">
        <el-button type="primary" class="qr-add-group" icon="el-icon-plus" @click="openGroupModal()">新增分组</el-button>
        <div v-loading="loading" class="qr-group-list">
          <div v-if="tree.length === 0" class="qr-group-empty">暂无分组</div>
          <div
            v-for="item in tree"
            :key="item.id"
            class="qr-group-item"
            :class="{ selected: selectedGroupId === item.id }"
            @click="selectGroup(item.id)"
          >
            <div class="qr-group-info">
              <div class="qr-group-name">{{ item.name }}</div>
              <div class="qr-group-count">{{ (item.replies && item.replies.length) || 0 }} 条</div>
            </div>
            <div class="qr-group-actions">
              <el-button type="text" size="small" @click.stop="openGroupModal(item)">改</el-button>
              <el-popconfirm title="删除分组与其下用语？" @confirm="handleDeleteGroup(item.id)">
                <el-button slot="reference" type="text" size="small" class="qr-danger" @click.stop>删</el-button>
              </el-popconfirm>
            </div>
          </div>
        </div>
      </div>

      <!-- 右侧用语列表 -->
      <div class="qr-content">
        <div class="qr-content-head">
          <div class="qr-content-title-wrap">
            <span class="qr-content-title">{{ selectedGroup ? `分组：${selectedGroup.name}` : '请选择左侧分组' }}</span>
            <el-popconfirm v-if="selectedRowKeys.length > 0" :title="`确定删除选中的 ${selectedRowKeys.length} 条用语？`" @confirm="handleBatchDelete">
              <el-button slot="reference" size="small" type="danger" icon="el-icon-delete">
                批量删除 ({{ selectedRowKeys.length }})
              </el-button>
            </el-popconfirm>
          </div>
          <el-button type="primary" icon="el-icon-plus" :disabled="!selectedGroupId" @click="openReplyModal()">新增用语</el-button>
        </div>

        <div v-if="!selectedGroupId" class="qr-empty">请先新增并选择分组</div>
        <el-table
          v-else
          :data="replies"
          v-loading="loading"
          row-key="id"
          size="small"
          @selection-change="onSelectionChange"
        >
          <el-table-column type="selection" width="40" />
          <el-table-column prop="title" label="标题" width="160" />
          <el-table-column label="内容" min-width="200">
            <template slot-scope="{ row }">
              <div class="qr-content-preview">
                <div v-html="safeContent(row.content)"></div>
              </div>
            </template>
          </el-table-column>
          <el-table-column prop="sortOrder" label="排序" width="72" />
          <el-table-column label="操作" width="140">
            <template slot-scope="{ row }">
              <el-button type="text" size="small" @click="openReplyModal(row)">编辑</el-button>
              <el-popconfirm title="确定删除？" @confirm="handleDeleteReply(row.id)">
                <el-button slot="reference" type="text" size="small" class="qr-danger">删除</el-button>
              </el-popconfirm>
            </template>
          </el-table-column>
        </el-table>
      </div>
    </div>

    <!-- 分组弹窗 -->
    <el-dialog :visible.sync="groupModalOpen" :title="editingGroupId != null ? '编辑分组' : '新增分组'" width="480px">
      <el-form ref="groupFormRef" :model="groupForm" :rules="groupRules" label-position="top">
        <el-form-item label="分组名称" prop="name">
          <el-input v-model="groupForm.name" maxlength="50" />
        </el-form-item>
        <el-form-item label="排序（数字越小越靠前）" prop="sortOrder">
          <el-input-number v-model="groupForm.sortOrder" :min="0" class="full-width" />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="groupModalOpen = false">取消</el-button>
        <el-button type="primary" @click="submitGroup">确定</el-button>
      </div>
    </el-dialog>

    <!-- 用语弹窗 -->
    <el-dialog :visible.sync="replyModalOpen" :title="editingReply ? '编辑用语' : '新增用语'" width="680px">
      <el-form ref="replyFormRef" :model="replyForm" :rules="replyRules" label-position="top">
        <el-form-item label="所属分组" prop="groupId">
          <el-select v-model="replyForm.groupId" class="full-width" placeholder="请选择分组">
            <el-option v-for="g in tree" :key="g.id" :label="g.name" :value="g.id" />
          </el-select>
        </el-form-item>
        <el-form-item label="标题（斜线搜索用）" prop="title">
          <el-input v-model="replyForm.title" maxlength="100" />
        </el-form-item>
        <el-form-item label="正文" prop="content">
          <el-input v-model="replyForm.content" type="textarea" :rows="6" />
        </el-form-item>
        <el-form-item label="排序" prop="sortOrder">
          <el-input-number v-model="replyForm.sortOrder" :min="0" class="full-width" />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="replyModalOpen = false">取消</el-button>
        <el-button type="primary" @click="submitReply">确定</el-button>
      </div>
    </el-dialog>

    <!-- 批量导入弹窗 -->
    <el-dialog
      :visible.sync="importModalOpen"
      title="批量导入常用语"
      :width="previewData && !importResult ? '860px' : '520px'"
      @closed="closeImportModal"
    >
      <!-- 第一步：上传 -->
      <div v-if="!previewData && !importResult" class="import-step">
        <el-alert type="info" show-icon title="使用说明" :closable="false">
          <ol class="import-tips">
            <li>先下载 Excel 模板，按格式填写数据</li>
            <li>分组名称若不存在会自动建立</li>
            <li>标题和正文为必填字段</li>
            <li>若标题与现有用语匹配，可选择覆盖</li>
          </ol>
        </el-alert>
        <el-button icon="el-icon-download" @click="handleDownloadTemplate">下载导入模板</el-button>
        <el-upload
          accept=".xlsx,.xls"
          :show-file-list="false"
          :auto-upload="false"
          :on-change="onImportFileChange"
        >
          <el-button icon="el-icon-upload2" :loading="importing" type="primary">选择 Excel 文件上传</el-button>
        </el-upload>
      </div>

      <!-- 第二步：预览 -->
      <div v-if="previewData && !importResult" class="import-step">
        <el-alert
          type="info"
          show-icon
          :message="`解析完成：${previewData.validCount} 条有效，${previewData.errorCount} 条错误`"
          :closable="false"
        />
        <el-table :data="previewData.rows" row-key="rowIndex" size="small" max-height="400">
          <el-table-column prop="rowIndex" label="行号" width="60" />
          <el-table-column label="分组" width="100">
            <template slot-scope="{ row }">{{ row.data.groupName || '-' }}</template>
          </el-table-column>
          <el-table-column label="标题" width="140">
            <template slot-scope="{ row }">{{ row.data.title || '-' }}</template>
          </el-table-column>
          <el-table-column label="正文摘要" min-width="180" show-overflow-tooltip>
            <template slot-scope="{ row }">
              {{ summarize(row.data.content) }}
            </template>
          </el-table-column>
          <el-table-column label="操作" width="230">
            <template slot-scope="{ row }">
              <span v-if="!row.valid" class="import-error">{{ row.error }}</span>
              <span v-else-if="row.matches.length === 0" class="import-new">新增</span>
              <el-select
                v-else
                size="small"
                class="full-width"
                :value="previewDecisions[row.rowIndex] || '__new__'"
                @change="(val) => setDecision(row.rowIndex, val)"
              >
                <el-option label="新增" value="__new__" />
                <el-option
                  v-for="m in row.matches"
                  :key="m.id"
                  :label="`覆盖：${m.title} [${m.groupOrCategoryName}] (ID:${m.id})`"
                  :value="m.id"
                />
              </el-select>
            </template>
          </el-table-column>
        </el-table>
        <div class="import-summary">
          共 {{ validRows.length }} 条有效：{{ newCount }} 条新增、{{ overwriteCount }} 条覆盖、{{ previewData.errorCount }} 条错误跳过
        </div>
      </div>

      <!-- 第三步：结果 -->
      <el-alert
        v-if="importResult"
        :type="importResult.failed > 0 ? 'warning' : 'success'"
        show-icon
        :message="`导入完成：成功 ${importResult.success} 条，失败 ${importResult.failed} 条`"
        :closable="false"
      >
        <ul v-if="importResult.errors.length > 0" class="import-errors">
          <li v-for="(e, i) in importResult.errors" :key="i">{{ e }}</li>
        </ul>
      </el-alert>

      <div slot="footer">
        <template v-if="previewData && !importResult">
          <el-button @click="resetImport">重新上传</el-button>
          <el-button
            type="primary"
            :loading="executing"
            :disabled="previewData.validCount === 0"
            @click="handleImportExecute"
          >
            确认导入 ({{ previewData.validCount }} 条)
          </el-button>
        </template>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  listQuickReplyTree,
  createQuickReplyGroup,
  updateQuickReplyGroup,
  deleteQuickReplyGroup,
  createQuickReply,
  updateQuickReply,
  deleteQuickReply,
  downloadQuickReplyTemplate,
  batchDeleteQuickReplies,
  exportQuickReplies,
  previewImportQuickReplies,
  executeImportQuickReplies
} from '@/api/customerService/quickReply'

function downloadBlob (blob, filename) {
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  window.URL.revokeObjectURL(url)
}

function pad (n) {
  return n < 10 ? '0' + n : '' + n
}

export default {
  name: 'QuickReplyList',
  data () {
    return {
      agentId: this.$store.state.customerService.agentId,
      tree: [],
      loading: false,
      selectedGroupId: null,

      groupModalOpen: false,
      replyModalOpen: false,
      editingGroupId: null,
      editingReply: null,
      groupForm: { name: '', sortOrder: 0 },
      replyForm: { groupId: undefined, title: '', content: '', sortOrder: 0 },
      groupRules: {
        name: [{ required: true, message: '请输入名称', trigger: 'blur' }]
      },
      replyRules: {
        groupId: [{ required: true, message: '请选择分组', trigger: 'change' }],
        title: [{ required: true, message: '请输入标题', trigger: 'blur' }],
        content: [{ required: true, message: '请输入内容', trigger: 'blur' }]
      },

      selectedRowKeys: [],

      importModalOpen: false,
      importing: false,
      importResult: null,
      exporting: false,
      previewData: null,
      previewDecisions: {},
      executing: false
    }
  },
  computed: {
    selectedGroup () {
      return this.tree.find(g => g.id === this.selectedGroupId) || null
    },
    replies () {
      return (this.selectedGroup && this.selectedGroup.replies) || []
    },
    validRows () {
      return (this.previewData && this.previewData.rows.filter(r => r.valid)) || []
    },
    overwriteCount () {
      return this.validRows.filter(r => this.previewDecisions[r.rowIndex] != null).length
    },
    newCount () {
      return this.validRows.length - this.overwriteCount
    }
  },
  created () {
    this.loadTree()
  },
  methods: {
    safeContent (content) {
      // 富文本仅保留白名单标签，避免 XSS
      const text = content || ''
      return text.replace(/<script[\s\S]*?<\/script>/gi, '').replace(/on\w+="[^"]*"/g, '')
    },
    async loadTree () {
      if (!this.agentId) {
        this.loading = false
        this.$message.warning('当前登录账号未关联客服档案，无法加载常用语')
        return
      }
      this.loading = true
      try {
        const res = await listQuickReplyTree(this.agentId)
        this.tree = res.result || []
        const stillExist = this.tree.some(g => g.id === this.selectedGroupId)
        if (!stillExist) {
          this.selectedGroupId = (this.tree[0] && this.tree[0].id) || null
        }
      } catch (e) {
        this.$message.error('加载常用语失败')
      } finally {
        this.loading = false
      }
    },
    selectGroup (id) {
      this.selectedGroupId = id
      this.selectedRowKeys = []
    },
    onSelectionChange (rows) {
      this.selectedRowKeys = rows.map(r => r.id)
    },
    // ===================== 分组维护 =====================
    openGroupModal (group) {
      this.editingGroupId = (group && group.id) || null
      this.groupForm = {
        name: (group && group.name) || '',
        sortOrder: (group && group.sortOrder) || 0
      }
      this.groupModalOpen = true
    },
    async submitGroup () {
      const formEl = this.$refs.groupFormRef
      if (!formEl) return
      await formEl.validate()
      try {
        const body = { name: this.groupForm.name, sortOrder: this.groupForm.sortOrder }
        if (this.editingGroupId != null) {
          await updateQuickReplyGroup(this.agentId, this.editingGroupId, body)
          this.$message.success('已更新分组')
        } else {
          await createQuickReplyGroup(this.agentId, body)
          this.$message.success('已新增分组')
        }
        this.groupModalOpen = false
        await this.loadTree()
      } catch (e) {
        this.$message.error((e && e.message) || '操作失败')
      }
    },
    async handleDeleteGroup (id) {
      try {
        await deleteQuickReplyGroup(this.agentId, id)
        this.$message.success('已删除分组')
        if (this.selectedGroupId === id) this.selectedGroupId = null
        await this.loadTree()
      } catch (e) {
        this.$message.error((e && e.message) || '删除失败')
      }
    },
    // ===================== 用语维护 =====================
    openReplyModal (item) {
      if (!this.selectedGroupId && !item) {
        this.$message.warning('请先选择或新增分组')
        return
      }
      this.editingReply = item || null
      this.replyForm = {
        groupId: (item && item.groupId) || this.selectedGroupId || undefined,
        title: (item && item.title) || '',
        content: (item && item.content) || '',
        sortOrder: (item && item.sortOrder) || 0
      }
      this.replyModalOpen = true
    },
    async submitReply () {
      const formEl = this.$refs.replyFormRef
      if (!formEl || this.replyForm.groupId == null) return
      await formEl.validate()
      try {
        const body = {
          groupId: this.replyForm.groupId,
          title: this.replyForm.title,
          content: this.replyForm.content,
          sortOrder: this.replyForm.sortOrder
        }
        if (this.editingReply) {
          await updateQuickReply(this.agentId, this.editingReply.id, body)
          this.$message.success('已更新用语')
        } else {
          await createQuickReply(this.agentId, body)
          this.$message.success('已新增用语')
        }
        this.replyModalOpen = false
        await this.loadTree()
      } catch (e) {
        this.$message.error((e && e.message) || '操作失败')
      }
    },
    async handleDeleteReply (id) {
      try {
        await deleteQuickReply(this.agentId, id)
        this.$message.success('已删除')
        await this.loadTree()
      } catch (e) {
        this.$message.error((e && e.message) || '删除失败')
      }
    },
    // ===================== 批量操作 =====================
    async handleDownloadTemplate () {
      try {
        const blob = await downloadQuickReplyTemplate(this.agentId)
        downloadBlob(blob, '常用语导入模板.xlsx')
      } catch (e) {
        this.$message.error('下载模板失败')
      }
    },
    openImportModal () {
      this.importResult = null
      this.previewData = null
      this.importModalOpen = true
    },
    closeImportModal () {
      this.previewData = null
      this.importResult = null
    },
    resetImport () {
      this.previewData = null
      this.importResult = null
    },
    onImportFileChange (file) {
      if (file && file.raw) this.handleImportPreview(file.raw)
    },
    async handleImportPreview (file) {
      this.importing = true
      this.importResult = null
      this.previewData = null
      this.previewDecisions = {}
      try {
        const res = await previewImportQuickReplies(this.agentId, file)
        const result = res.result
        this.previewData = result
        const decisions = {}
        result.rows.forEach(row => {
          if (row.valid) decisions[row.rowIndex] = null
        })
        this.previewDecisions = decisions
      } catch (e) {
        this.$message.error((e && e.message) || '预览失败')
      } finally {
        this.importing = false
      }
    },
    setDecision (rowIndex, val) {
      this.$set(this.previewDecisions, rowIndex, val === '__new__' ? null : val)
    },
    async handleImportExecute () {
      if (!this.previewData) return
      const items = this.previewData.rows
        .filter(row => row.valid)
        .map(row => ({
          data: row.data,
          overwriteId: this.previewDecisions[row.rowIndex] || null
        }))
      if (items.length === 0) {
        this.$message.warning('没有可导入的数据')
        return
      }
      this.executing = true
      try {
        const res = await executeImportQuickReplies(this.agentId, items)
        const result = res.result
        this.importResult = result
        this.previewData = null
        if (result.success > 0) {
          this.$message.success(`成功导入 ${result.success} 条`)
          await this.loadTree()
        }
      } catch (e) {
        this.$message.error((e && e.message) || '导入失败')
      } finally {
        this.executing = false
      }
    },
    async handleBatchDelete () {
      if (this.selectedRowKeys.length === 0) return
      try {
        await batchDeleteQuickReplies(this.agentId, this.selectedRowKeys.map(String))
        this.$message.success(`已删除 ${this.selectedRowKeys.length} 条`)
        this.selectedRowKeys = []
        await this.loadTree()
      } catch (e) {
        this.$message.error((e && e.message) || '批量删除失败')
      }
    },
    async handleExport () {
      this.exporting = true
      try {
        const blob = await exportQuickReplies(this.agentId)
        const d = new Date()
        downloadBlob(blob, `我的常用语_${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}_${pad(d.getHours())}${pad(d.getMinutes())}.xlsx`)
      } catch (e) {
        this.$message.error('导出失败')
      } finally {
        this.exporting = false
      }
    },
    summarize (c) {
      const s = c || ''
      return s.length > 60 ? s.substring(0, 60) + '…' : s
    }
  }
}
</script>

<style scoped>
.qr-page {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.page-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.page-title {
  font-size: 20px;
  font-weight: 600;
}

.page-actions {
  display: flex;
  gap: 8px;
}

.page-desc {
  color: #909399;
  margin-bottom: 16px;
}

.qr-layout {
  flex: 1;
  min-height: 420px;
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  display: flex;
  overflow: hidden;
}

.qr-sider {
  width: 220px;
  background: #fafafa;
  border-right: 1px solid #ebeef5;
  padding: 12px;
}

.qr-add-group {
  width: 100%;
  margin-bottom: 12px;
}

.qr-group-list {
  min-height: 120px;
}

.qr-group-empty {
  color: #c0c4cc;
  font-size: 13px;
  text-align: center;
  padding: 16px 0;
}

.qr-empty {
  color: #c0c4cc;
  font-size: 13px;
  text-align: center;
  padding: 60px 0;
}

.qr-group-item {
  cursor: pointer;
  padding: 8px 10px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
}

.qr-group-item:hover {
  background: #f5f7fa;
}

.qr-group-item.selected {
  background: #e0e7ff;
}

.qr-group-name {
  font-weight: 500;
  font-size: 14px;
}

.qr-group-count {
  font-size: 12px;
  color: #909399;
}

.qr-group-actions {
  display: flex;
  flex-shrink: 0;
}

.qr-danger {
  color: #f56c6c;
}

.qr-content {
  flex: 1;
  padding: 16px;
  min-width: 0;
}

.qr-content-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.qr-content-title-wrap {
  display: flex;
  align-items: center;
  gap: 12px;
}

.qr-content-title {
  font-weight: 600;
}

.qr-content-preview {
  max-height: 60px;
  overflow: hidden;
  font-size: 13px;
}

.full-width {
  width: 100%;
}

.import-step {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.import-tips {
  margin: 4px 0 0;
  padding-left: 20px;
}

.import-summary {
  color: #909399;
  font-size: 13px;
}

.import-error {
  color: #f56c6c;
  font-size: 12px;
}

.import-new {
  color: #67c23a;
}

.import-errors {
  margin: 8px 0 0;
  padding-left: 20px;
}
</style>